/**
 * Floating Button Component
 *
 * Customizable floating action button that triggers the bug report dialog.
 * Supports multiple positions, brand colors, and mobile-optimized sizing.
 *
 * @module FloatingButton
 */

import React, { useState, useMemo, CSSProperties, useRef, useEffect } from 'react';
import { BugReportDialog } from './BugReportDialog';
import type { WidgetConfig } from '@wds/bug-reporter-types';

/**
 * Calculate relative luminance to determine if color is light or dark
 */
function isLightColor(hexColor: string): boolean {
  if (hexColor === 'transparent') return true;
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16) / 255;
  const g = parseInt(hex.substr(2, 2), 16) / 255;
  const b = parseInt(hex.substr(4, 2), 16) / 255;
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance > 0.6;
}

/**
 * Position of the floating button on the page
 */
export type FloatingButtonPosition = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';

/**
 * Props for FloatingButton component
 */
export interface FloatingButtonProps {
  /** Position of the button */
  position: FloatingButtonPosition;

  /** Brand color for the button (CSS color value) */
  brandColor: string;

  /** Custom SVG logo (sanitized, optional) */
  logoSvg: string | null;

  /** Button text */
  buttonText: string;

  /** API key for authentication */
  apiKey: string;

  /** Base URL for bug reporter API */
  apiBaseUrl: string;

  /** Cloudflare Turnstile site key */
  turnstileSiteKey: string;

  /** Full widget configuration */
  widgetConfig: WidgetConfig;
}

/**
 * Map position to CSS class names
 */
const POSITION_CLASSES: Record<FloatingButtonPosition, string> = {
  'bottom-right': 'bug-reporter-button--bottom-right',
  'bottom-left': 'bug-reporter-button--bottom-left',
  'top-right': 'bug-reporter-button--top-right',
  'top-left': 'bug-reporter-button--top-left',
};

/**
 * Floating action button for bug reporting
 *
 * Features:
 * - Customizable position (4 corners)
 * - Brand color theming
 * - Pulse animation for attention
 * - Mobile-responsive (min 48x48px touch target)
 * - Z-index management (stays on top)
 * - Accessibility support (ARIA labels, focus states)
 *
 * @example
 * ```tsx
 * <FloatingButton
 *   position="bottom-right"
 *   brandColor="#3b82f6"
 *   apiKey="bw_live_..."
 *   apiBaseUrl="https://api.example.com"
 *   turnstileSiteKey="your-site-key"
 * />
 * ```
 */
export const FloatingButton: React.FC<FloatingButtonProps> = ({
  position,
  brandColor,
  logoSvg,
  buttonText,
  apiKey,
  apiBaseUrl,
  turnstileSiteKey,
  widgetConfig,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Dragging state
  const [isDragging, setIsDragging] = useState(false);
  const [isPotentialDrag, setIsPotentialDrag] = useState(false); // Mouse down but not yet past threshold
  const [position_x, setPositionX] = useState<number | null>(null);
  const [position_y, setPositionY] = useState<number | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Drag threshold tracking - prevents click/drag conflicts
  const dragStartPos = useRef<{ x: number; y: number } | null>(null);
  const hasDraggedRef = useRef(false);
  const DRAG_THRESHOLD = 5; // pixels - minimum movement to start drag

  // Load saved position from localStorage
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem('bug-reporter-button-position');
      if (saved) {
        const { x, y } = JSON.parse(saved);
        setPositionX(x);
        setPositionY(y);
      }
    } catch (e) {
      console.warn('[BugReporter] Failed to load button position:', e);
    }
  }, []);

  // Handle drag start - records initial position, actual drag starts after threshold
  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    // Ignore if dialog is open
    if (isDialogOpen) return;

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDragOffset({
        x: clientX - rect.left,
        y: clientY - rect.top,
      });
      // Record start position for threshold check
      dragStartPos.current = { x: clientX, y: clientY };
      hasDraggedRef.current = false;
      // Mark as potential drag - waiting for movement past threshold
      setIsPotentialDrag(true);
      e.preventDefault();
    }
  };

  // Handle drag move with threshold check
  useEffect(() => {
    // Listen for move events when drag might be starting (isPotentialDrag) OR when dragging
    if (!isPotentialDrag && !isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      // Check if we need to start dragging (threshold check)
      if (isPotentialDrag && !isDragging && dragStartPos.current) {
        const dx = e.clientX - dragStartPos.current.x;
        const dy = e.clientY - dragStartPos.current.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance > DRAG_THRESHOLD) {
          setIsDragging(true);
          hasDraggedRef.current = true;
        } else {
          return; // Not past threshold yet
        }
      }

      // Actual dragging logic
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      // Clamp to viewport boundaries
      const maxX = window.innerWidth - 56; // Button is 56px wide
      const maxY = window.innerHeight - 56; // Button is 56px tall

      setPositionX(Math.max(0, Math.min(newX, maxX)));
      setPositionY(Math.max(0, Math.min(newY, maxY)));
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!e.touches[0]) return;

      // Check if we need to start dragging (threshold check)
      if (isPotentialDrag && !isDragging && dragStartPos.current) {
        const dx = e.touches[0].clientX - dragStartPos.current.x;
        const dy = e.touches[0].clientY - dragStartPos.current.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance > DRAG_THRESHOLD) {
          setIsDragging(true);
          hasDraggedRef.current = true;
        } else {
          return; // Not past threshold yet
        }
      }

      const newX = e.touches[0].clientX - dragOffset.x;
      const newY = e.touches[0].clientY - dragOffset.y;

      // Clamp to viewport boundaries
      const maxX = window.innerWidth - 56;
      const maxY = window.innerHeight - 56;

      setPositionX(Math.max(0, Math.min(newX, maxX)));
      setPositionY(Math.max(0, Math.min(newY, maxY)));
    };

    const handleDragEnd = () => {
      setIsDragging(false);
      setIsPotentialDrag(false); // Clear potential drag state
      dragStartPos.current = null; // Clear start position

      // Save position to sessionStorage
      if (position_x !== null && position_y !== null) {
        try {
          sessionStorage.setItem('bug-reporter-button-position', JSON.stringify({
            x: position_x,
            y: position_y,
          }));
        } catch (e) {
          console.warn('[BugReporter] Failed to save button position:', e);
        }
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('mouseup', handleDragEnd);
    document.addEventListener('touchend', handleDragEnd);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('mouseup', handleDragEnd);
      document.removeEventListener('touchend', handleDragEnd);
    };
  }, [isPotentialDrag, isDragging, dragOffset, position_x, position_y]);

  // Handle window resize - reposition button if it becomes invisible
  useEffect(() => {
    const handleResize = () => {
      if (position_x === null || position_y === null) return;

      // Check if button is still visible in the viewport
      // Button is 56px (button size) and repositioned if outside viewport
      const isOutsideViewport =
        position_x > window.innerWidth - 56 ||
        position_y > window.innerHeight - 56;

      if (isOutsideViewport) {
        // Reset to default corner position
        setPositionX(null);
        setPositionY(null);
        try {
          sessionStorage.removeItem('bug-reporter-button-position');
        } catch (e) {
          console.warn('[BugReporter] Failed to clear button position:', e);
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [position_x, position_y]);

  // Extract config values with defaults
  const buttonTooltip = widgetConfig?.buttonTooltip ?? buttonText;
  const buttonShadow = widgetConfig?.buttonShadow ?? true;
  const buttonBorderColor = widgetConfig?.buttonBorderColor ?? 'transparent';
  const hoverEffect = widgetConfig?.hoverEffect ?? 'no-effect';
  const hoverScale = widgetConfig?.hoverScale ?? true;
  const iconScale = widgetConfig?.iconScale ?? 1.0;

  // Calculate icon size (base is 24px)
  const iconSize = Math.round(24 * iconScale);

  // Calculate text color based on background luminance
  const isTransparent = brandColor === 'transparent';
  const textColor = isTransparent ? '#1f2937' : (isLightColor(brandColor) ? '#1f2937' : '#ffffff');

  // Calculate button styles
  const buttonStyles = useMemo<CSSProperties>(() => {
    const styles: CSSProperties = {
      backgroundColor: brandColor,
      color: textColor,
      userSelect: isDragging ? 'none' : 'auto',
      cursor: isDragging ? 'grabbing' : 'grab',
      // Smooth transitions for hover effects
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    };

    // Apply custom position if available
    if (position_x !== null && position_y !== null) {
      styles.left = `${position_x}px`;
      styles.top = `${position_y}px`;
      styles.bottom = 'auto';
      styles.right = 'auto';
    }

    // Border
    if (buttonBorderColor !== 'transparent') {
      styles.border = `1px solid ${buttonBorderColor}`;
    }

    // Shadow (override CSS default if disabled)
    if (!buttonShadow) {
      styles.boxShadow = 'none';
    }

    // Hover states
    if (isHovered && !isDragging) {
      // Scale effect
      if (hoverScale) {
        styles.transform = 'scale(1.05)';
      }

      // Hover effect
      if (hoverEffect === 'glow') {
        const glowColor = isTransparent ? 'rgba(99, 102, 241, 0.5)' : brandColor;
        styles.boxShadow = `0 0 20px ${glowColor}`;
      } else if (hoverEffect === 'shadow') {
        styles.boxShadow = '0 10px 25px rgba(0, 0, 0, 0.3)';
      }
    }

    return styles;
  }, [brandColor, textColor, buttonBorderColor, buttonShadow, isHovered, hoverScale, hoverEffect, isTransparent, isDragging, position_x, position_y]);

  /**
   * Handle button click - opens bug report dialog
   * Skips if user just finished dragging (to prevent accidental opens)
   */
  const handleClick = () => {
    // If user just dragged the button, don't open dialog
    if (hasDraggedRef.current) {
      hasDraggedRef.current = false;
      return;
    }
    setIsDialogOpen(true);
  };

  /**
   * Handle dialog close
   */
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  return (
    <>
      {/* Floating Button */}
      <button
        ref={buttonRef}
        type="button"
        className={`bug-reporter-button ${POSITION_CLASSES[position as FloatingButtonPosition]}`}
        style={buttonStyles}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        aria-label={buttonText}
        title={buttonTooltip || buttonText}
        data-bug-reporter-button
      >
        {/* Custom SVG Logo or Default Bug Icon */}
        {logoSvg ? (
          <div
            className="bug-reporter-button__icon"
            dangerouslySetInnerHTML={{ __html: logoSvg }}
            aria-hidden="true"
            style={{
              width: iconSize,
              height: iconSize,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />
        ) : (
          <svg
            className="bug-reporter-button__icon"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            style={{
              width: iconSize,
              height: iconSize,
            }}
          >
            {/* Bug icon path */}
            <path d="M8 2h8" />
            <path d="M9 9V2" />
            <path d="M15 9V2" />
            <path d="M7.5 9a5.5 5.5 0 1 0 9 0" />
            <path d="M12 13v8" />
            <path d="M12 21a9 9 0 0 1-9-9" />
            <path d="M12 21a9 9 0 0 0 9-9" />
            <path d="M12 13h.01" />
            <circle cx="12" cy="12" r="1" />
            <path d="M7.5 4.2c-.3-.5-.9-.7-1.3-.4C3.6 5.2 2 8.1 2 11.5c0 .3.2.5.5.5h2c.3 0 .5-.2.5-.5 0-2.2 1-4.1 2.5-5.3.4-.3.5-.9.2-1.3" />
            <path d="M21.5 11.5c0-3.4-1.6-6.3-4.2-7.7-.4-.3-1-.1-1.3.4-.3.4-.2 1 .2 1.3C17.8 6.4 19 8.3 19 10.5c0 .3.2.5.5.5h2c.3 0 .5-.2.5-.5" />
          </svg>
        )}

        {/* Button Text */}
        {buttonText && (
          <span className="bug-reporter-button__text">{buttonText}</span>
        )}

        {/* Pulse animation ring */}
        <span className="bug-reporter-button__pulse" aria-hidden="true" />
      </button>

      {/* Bug Report Dialog */}
      <BugReportDialog
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        apiKey={apiKey}
        apiBaseUrl={apiBaseUrl}
        turnstileSiteKey={turnstileSiteKey}
        widgetConfig={widgetConfig}
      />
    </>
  );
};
